import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { WizardStepRouter } from './WizardStepRouter';
import { WizardNavigation } from './WizardNavigation';
import { WizardProgress } from './WizardProgress';
import { useLevelUpWizard } from '../../hooks/useLevelUpWizard';
import { CharacterData } from '../../types/character';
import styles from './LevelUpWizard.module.css';

export interface LevelUpWizardProps {
  character: CharacterData;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (updatedCharacter: CharacterData) => void;
}

/**
 * Main Level-Up Wizard Modal Container
 * Manages the entire level-up workflow from start to finish
 */
export const LevelUpWizard: React.FC<LevelUpWizardProps> = ({
  character,
  isOpen,
  onClose,
  onComplete
}) => {
  const {
    session,
    initializeSession,
    loadExistingSession,
    cancelWizard
  } = useLevelUpWizard();

  // Initialize or load session when modal opens
  useEffect(() => {
    if (isOpen && !session) {
      // Try to load existing session first, then initialize if not found
      loadExistingSession(character.id || '', character).then(loaded => {
        // If no existing session, create new one
        if (!loaded) {
          initializeSession(character);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, character.id, session]);

  // Handle cancel
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All progress will be lost.')) {
      cancelWizard();
      onClose();
    }
  };

  // Handle completion - just close the wizard (level-up already committed on summary step)
  const handleComplete = () => {
    onClose();
  };

  if (!isOpen) return null;

  // Render modal using portal to ensure it's at document body level
  // This prevents z-index and positioning issues from parent containers
  return createPortal(
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header with progress */}
        <div className={styles.wizardHeader}>
          <h2 className={styles.title}>Level Up Character</h2>
          <button
            type="button"
            onClick={handleCancel}
            className={styles.closeButton}
            aria-label="Close wizard"
          >
            ×
          </button>
        </div>

        <WizardProgress />

        {/* Main content area */}
        <div className={styles.wizardContent}>
          <WizardStepRouter />
        </div>

        {/* Footer with navigation */}
        <WizardNavigation
          onCancel={handleCancel}
          onClose={handleComplete}
          onComplete={onComplete}
        />
      </div>
    </div>,
    document.body
  );
};
